/**
 * Combined Listings & High-Variant Product Support
 *
 * Combined listings group several *separate* child products under one listing.
 * Each value of the cross-product option (the dropdown whose values carry a
 * `data-product-url`) actually belongs to a different product, whose variant
 * data is NOT present on the current page. This script makes that work on every
 * child page:
 *
 * 1. Keep cross-product options selectable. The stock Superstore variant
 *    component (superstore.js) builds its option model from the *current*
 *    product's own variants only, so it disables the cross-product values (they
 *    have no local variant). We re-enable any value carrying `data-product-url`
 *    and keep it enabled even after Superstore re-runs its reconciliation.
 *
 * 2. When a cross-product value is chosen, navigate to that sibling product (in
 *    the current locale). We intentionally do a FULL page navigation rather than
 *    an AJAX section swap: the sibling page is then rendered entirely by the
 *    server, so the description, attributes, technical details and — crucially —
 *    third-party widgets like the Judge.me reviews come up correctly and in the
 *    right language. An in-place AJAX swap left those widgets un-rendered
 *    (Judge.me only initialises once, on first page load), so we favour
 *    correctness over avoiding the reload.
 */
(function () {
  'use strict';

  var CONTAINER_SELECTOR = '[data-product-options-container]';
  var OPTION_VALUE_SELECTOR =
    'option[data-product-url], input[type="radio"][data-product-url]';

  function getPathname(url) {
    if (!url) return null;
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (e) {
      return url.split('?')[0];
    }
  }

  // The cross-product option URLs (`data-product-url`) are canonical, locale-less
  // paths (e.g. `/products/x`). On a localized storefront the current page lives
  // under a locale/market root (e.g. `/de/products/x`), exposed as
  // `Shopify.routes.root` ("/" or "/de/"). Navigating to the bare path would drop
  // the customer to the default language, so we re-apply the current root.
  function localizedPath(pathname) {
    if (!pathname) return pathname;
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root === '/') return pathname;
    root = root.replace(/\/+$/, ''); // "/de/" -> "/de"
    // Don't double-prefix if the path is already localized.
    if (pathname === root || pathname.indexOf(root + '/') === 0) return pathname;
    return root + pathname;
  }

  /* ------------------------------------------------------------------ *
   * 1. Keep cross-product option values selectable
   * ------------------------------------------------------------------ */

  function enableSiblingOptions() {
    var elements = document.querySelectorAll(OPTION_VALUE_SELECTOR);
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (el.disabled) el.disabled = false;
      if (el.hasAttribute('disabled')) el.removeAttribute('disabled');
      el.classList.remove('disabled', 'option-value--unavailable');
    }
  }

  var observers = [];

  function disconnectObservers() {
    for (var i = 0; i < observers.length; i++) observers[i].disconnect();
    observers = [];
  }

  function watchAndKeepEnabled() {
    enableSiblingOptions();
    disconnectObservers();
    if (!('MutationObserver' in window)) return;

    var containers = document.querySelectorAll(CONTAINER_SELECTOR);
    for (var i = 0; i < containers.length; i++) {
      var observer = new MutationObserver(function (mutations, obs) {
        obs.disconnect(); // ignore our own mutations
        enableSiblingOptions();
        observeContainer(obs, obs.__clTarget);
      });
      observeContainer(observer, containers[i]);
      observers.push(observer);
    }
  }

  function observeContainer(observer, target) {
    if (!target || !target.isConnected) return;
    observer.__clTarget = target;
    observer.observe(target, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });
  }

  /* ------------------------------------------------------------------ *
   * 2. Navigate to the sibling product on a cross-product selection
   * ------------------------------------------------------------------ */

  // Capture phase: Superstore binds a `change` listener directly on each option
  // control and, for a cross-product value it can't resolve locally, reverts the
  // control to a local value. Listening in the capture phase lets us read the
  // customer's real selection first and stop the event before Superstore reverts.
  document.addEventListener(
    'change',
    function (event) {
      var target = event.target;
      var optionEl = null;

      if (target.tagName === 'SELECT' && target.dataset.productOption !== undefined) {
        optionEl = target.options[target.selectedIndex];
      } else if (
        target.tagName === 'INPUT' &&
        target.type === 'radio' &&
        target.dataset.optionValueId
      ) {
        optionEl = target;
      }

      if (!optionEl) return;

      var productUrl = optionEl.dataset.productUrl;
      if (!productUrl) return; // in-product axis → let the theme handle it

      // Resolve to the current locale so the sibling loads in-language.
      var targetPath = localizedPath(getPathname(productUrl));
      var currentPath = window.location.pathname.split('?')[0];
      if (!targetPath || targetPath === currentPath) return; // same product

      // Cross-product selection: stop Superstore's revert and navigate to the
      // sibling. A full navigation renders the whole sibling page server-side
      // (incl. Judge.me reviews, in the right language).
      event.stopPropagation();
      window.location.assign(targetPath);
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchAndKeepEnabled);
  } else {
    watchAndKeepEnabled();
  }
})();
